// Toasts with optional Undo (MYTV01 "undo during the current interaction", HOME02 undo). Owner: WP04.
// A tiny module store: call toast() from anywhere; the Shell renders <Toaster/> once.
import { useSyncExternalStore } from 'react';
import { X } from 'lucide-react';
import { Button, IconButton } from './Button.tsx';

export type ToastInput = {
  message: string;
  /** e.g. { label: 'Undo', onClick: restore } */
  action?: { label: string; onClick(): void };
  /** ms; default 6000 (8000 with an action so Undo is reachable). */
  duration?: number;
  tone?: 'neutral' | 'error';
};
type ToastItem = ToastInput & { id: number };

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function dismissToast(id: number) {
  items = items.filter((t) => t.id !== id);
  emit();
}
/** Show a toast; returns its id. At most 3 are visible (oldest dropped). */
export function toast(input: ToastInput): number {
  const id = ++seq;
  items = [...items.slice(-2), { ...input, id }];
  emit();
  setTimeout(() => dismissToast(id), input.duration ?? (input.action ? 8000 : 6000));
  return id;
}

const subscribe = (l: () => void) => { listeners.add(l); return () => listeners.delete(l); };
const snapshot = () => items;

export function Toaster() {
  const list = useSyncExternalStore(subscribe, snapshot, snapshot);
  return (
    <div className="ui-toaster" role="region" aria-label="Notifications">
      <div aria-live="polite" aria-atomic="false" className="ui-toaster__stack">
        {list.map((t) => (
          <div key={t.id} className={`ui-toast ui-toast--${t.tone ?? 'neutral'}`} role={t.tone === 'error' ? 'alert' : 'status'}>
            <span className="ui-toast__msg">{t.message}</span>
            {t.action ? (
              <Button size="sm" variant="ghost" className="ui-toast__action" onClick={() => { t.action!.onClick(); dismissToast(t.id); }}>
                {t.action.label}
              </Button>
            ) : null}
            <IconButton label="Dismiss notification" icon={<X size={16} />} size="sm" onClick={() => dismissToast(t.id)} />
          </div>
        ))}
      </div>
    </div>
  );
}
